/**
 * routeSnap.ts — where a point sits relative to the drawn road route.
 *
 * Two jobs, one geometry:
 *  • A pin dropped right beside the route snaps ONTO the route line, so the
 *    reroute stays on the highway instead of hairpinning out to the click and
 *    doubling back (the "spur at Orbost" problem).
 *  • A search suggestion says how far off the route it is ("On your route",
 *    "3 km off route"), which a plain geocoder list can't tell you.
 *
 * Pure and planar (an equirectangular projection around each segment), which is
 * accurate to well under 1% at the few-km scale these numbers are shown at.
 */

export interface LatLngLike {
  lat: number;
  lng: number;
}

const R_KM = 6371;
const toRad = (d: number) => (d * Math.PI) / 180;

const finite = (p: LatLngLike | null | undefined): p is LatLngLike =>
  !!p && Number.isFinite(p.lat) && Number.isFinite(p.lng);

/** Great-circle distance in km. */
export function haversineKm(a: LatLngLike, b: LatLngLike): number {
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return R_KM * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
}

export interface PathHit {
  /** Closest point on the path. */
  lat: number;
  lng: number;
  /** Distance from the query point to that closest point. */
  km: number;
  /** Index of the segment start vertex the point falls on. */
  segment: number;
}

/**
 * Closest point on a polyline to `p`. Null for an empty path or bad input.
 * Each segment is projected flat around its own latitude, so long routes
 * don't accumulate projection error.
 */
export function nearestOnPath(path: LatLngLike[], p: LatLngLike): PathHit | null {
  if (!finite(p) || !path || !path.length) return null;
  const pts = path.filter(finite);
  if (!pts.length) return null;
  if (pts.length === 1) return { lat: pts[0].lat, lng: pts[0].lng, km: haversineKm(p, pts[0]), segment: 0 };
  let best: PathHit | null = null;
  for (let i = 0; i < pts.length - 1; i += 1) {
    const a = pts[i];
    const b = pts[i + 1];
    const kx = Math.cos(toRad((a.lat + b.lat + p.lat) / 3));
    const ax = a.lng * kx, ay = a.lat;
    const bx = b.lng * kx, by = b.lat;
    const px = p.lng * kx, py = p.lat;
    const dx = bx - ax, dy = by - ay;
    const len2 = dx * dx + dy * dy;
    const t = len2 === 0 ? 0 : Math.max(0, Math.min(1, ((px - ax) * dx + (py - ay) * dy) / len2));
    const hit = { lat: a.lat + (b.lat - a.lat) * t, lng: a.lng + (b.lng - a.lng) * t };
    const km = haversineKm(p, hit);
    if (!best || km < best.km) best = { ...hit, km, segment: i };
  }
  return best;
}

/** A dropped pin this close to the route line is "on the route". */
export const SNAP_TO_ROUTE_KM = 8;

/**
 * Where a dropped pin should actually go: onto the route line when it was
 * dropped beside it (within `maxKm`), otherwise exactly where it was dropped.
 * `snapped` says which happened.
 */
export function snapPinToRoute(
  path: LatLngLike[] | null | undefined,
  p: LatLngLike,
  maxKm = SNAP_TO_ROUTE_KM
): LatLngLike & { snapped: boolean } {
  const hit = path && path.length >= 2 ? nearestOnPath(path, p) : null;
  if (hit && hit.km <= maxKm) return { lat: hit.lat, lng: hit.lng, snapped: true };
  return { lat: p.lat, lng: p.lng, snapped: false };
}

/** Within this of the line, a place counts as "on your route". */
export const ON_ROUTE_KM = 1.5;
/** Beyond this, "N km off route" stops being useful (it's somewhere else). */
export const MAX_OFF_ROUTE_KM = 150;

/**
 * "On your route" / "3 km off route" for a suggestion row, or null when there's
 * no road route yet or the place is nowhere near it.
 */
export function offRouteLabel(
  path: LatLngLike[] | null | undefined,
  p: Partial<LatLngLike>,
  maxKm: number = MAX_OFF_ROUTE_KM
): string | null {
  if (!path || path.length < 2 || typeof p.lat !== 'number' || typeof p.lng !== 'number') return null;
  const hit = nearestOnPath(path, { lat: p.lat, lng: p.lng });
  if (!hit || hit.km > maxKm) return null;
  if (hit.km <= ON_ROUTE_KM) return 'On your route';
  return `${Math.round(hit.km).toLocaleString('en-AU')} km off route`;
}

/**
 * Extra driving for adding `p` between `a` and `b`, in straight-line km
 * (a ≈ figure: the caller marks it so). Never negative.
 */
export function detourKm(a: LatLngLike, b: LatLngLike, p: LatLngLike): number {
  if (!finite(a) || !finite(b) || !finite(p)) return 0;
  return Math.max(0, haversineKm(a, p) + haversineKm(p, b) - haversineKm(a, b));
}

/**
 * The point fraction `t` of the way along the road line between where stops
 * `a` and `b` meet it. Null if the line doesn't run between them.
 */
export function pointAlongRoute(
  path: LatLngLike[] | null | undefined,
  a: LatLngLike,
  b: LatLngLike,
  t: number
): LatLngLike | null {
  if (!path || path.length < 2) return null;
  const ha = nearestOnPath(path, a);
  const hb = nearestOnPath(path, b);
  if (!ha || !hb || hb.segment < ha.segment) return null;
  const pts: LatLngLike[] = [
    { lat: ha.lat, lng: ha.lng },
    ...path.slice(ha.segment + 1, hb.segment + 1),
    { lat: hb.lat, lng: hb.lng },
  ];
  let total = 0;
  for (let i = 1; i < pts.length; i += 1) total += haversineKm(pts[i - 1], pts[i]);
  if (total <= 0) return null;
  let want = total * Math.max(0, Math.min(1, t));
  for (let i = 1; i < pts.length; i += 1) {
    const d = haversineKm(pts[i - 1], pts[i]);
    if (want <= d && d > 0) {
      const f = want / d;
      return {
        lat: pts[i - 1].lat + (pts[i].lat - pts[i - 1].lat) * f,
        lng: pts[i - 1].lng + (pts[i].lng - pts[i - 1].lng) * f,
      };
    }
    want -= d;
  }
  return pts[pts.length - 1];
}
